// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved

import React, { PureComponent, Fragment } from 'react'
import PropTypes from 'prop-types'
import { Query, Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'
import DatePicker from 'react-datepicker'

import {
  DynamicPicklist,
  Input,
  SvgIcon,
  Button,
  KeyValueTable,
  BulkSMSMessageComposer,
  Modal,
  Warning
} from '../../'

import { CHANNEL_SMS_MESSAGEPLUS } from '../../../constants'

import { decodeBase64 } from '../../../utils'


const BULKMESSAGE_MUTATION = gql`
  mutation($input: BulkMessageMutationInput!) {
    bulkmessageMutate(input: $input) {
      bulkmessage {
        id
        description
        content
      }
    }
  }
`

const QUERY_EMPLOYER_BULKMESSAGE_AUDIENCES = gql`
  query($employerId: UUID!) {
    bulkmessageaudiences(employer_Id: $employerId) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`

const QUERY_AUDIENCE = gql`
  query($id: ID!) {
    bulkmessageaudience(id: $id) {
      id
      identities(first: 5) {
        edges {
          node {
            id
            firstName
            lastName
            fullName
          }
        }
      }
    }
  }
`


export class EmployerBulkSMSMessageCompose extends PureComponent {
  static propTypes = {
    employerId: PropTypes.string.isRequired,
    employerIdentityId: PropTypes.string.isRequired,
    goToBulkMessageDetailPage: PropTypes.func.isRequired,
    goToAudienceListingPage: PropTypes.func.isRequired,
  }

  state = {
    description: '',
    audience: '',
    language: 'eng_za',
    messageContent: '',
    scheduledAt: null,
    submitInProgress: false,
    submitComplete: false,
    editScheduledAt: false,
    showPreviewModal: false,
    channels: [CHANNEL_SMS_MESSAGEPLUS]
  }

  createBulkMessage = (bulkmessageMutate, refetch) => {
    this.setState(
      { submitInProgress: true },
      () => {
        bulkmessageMutate({
          variables: {
            input: {
              description: this.state.description,
              content: this.state.messageContent,
              status: 'draft',
              scheduledAt: this.state.scheduledAt,
              fromIdentity: this.props.employerIdentityId,
              audience: this.state.audience,
              channels: this.state.channels.map(channelId => decodeBase64(channelId))
            }
          }
        })
        .then(({ data }) => {
          this.setState(
            { submitInProgress: false, submitComplete: true },
            () => {
              this.props.history.push(
                `/employer/${this.props.match.params.employerId}/communications/bulk/${data.bulkmessageMutate.bulkmessage.id}`
              )
              refetch()
            }
          )

        })
        .catch(() => this.setState({ submitInProgress: false, submitComplete: false }))
      })

  }

  toggleEditScheduledAt = () => (
    this.setState(prevState => ({ editScheduledAt: !prevState.editScheduledAt }))
  )

  updateScheduleAt = (scheduledAt) => this.setState({ scheduledAt })

  requiredFieldsPopulated = () => (
    this.state.audience &&
    this.state.description.length > 0 && this.state.description.length <= 50 &&
    this.state.messageContent.length > 0
  )

  togglePreviewModal = () => (
    this.setState(prevState => (
      { showPreviewModal: !prevState.showPreviewModal }
    ))
  )

  handleChannelChange = (channelId) => {
    let channels = [...this.state.channels]
    const index = channels.indexOf(channelId)
    if (index > -1) {
      channels.splice(index, 1)
    } else {
      channels.push(channelId)
    }
    this.setState({ channels })
  }

  render() {
    const { description, audience, channels, messageContent, scheduledAt } = this.state

    return (
      <div className='slds-p-around_small slds-align_absolute-center slds-size_2-of-3' style={{ display: 'block' }}>
          <div className='u-font-size-larger slds-m-top_medium slds-m-bottom_large'>
            <b>Create a new bulk message</b>
          </div>
          <Query
            query={QUERY_EMPLOYER_BULKMESSAGE_AUDIENCES}
            variables={{ employerId: decodeBase64(this.props.employerId) }}>

            {({ loading, error, data, refetch }) => {
              if (loading) {
                return null
              }

              if (error) {
                return <Warning>Failed to fetch audience data</Warning>
              }

              if (data.bulkmessageaudiences.edges.length === 0) {
                return (
                  <Warning>
                    At least one audience needs to be defined in order to send a bulk message.
                    <Button
                      type='reset'
                      onClick={this.props.goToAudienceListingPage}
                      className='slds-p-left_small'>
                      Add audience?
                    </Button>
                  </Warning>
                )
              } else {
                return (
                  <Fragment>
                    <KeyValueTable
                      data={{
                        description: <Input
                          value={description}
                          onChange={e => this.setState({ description: e.target.value })}
                          hasError={description.length > 50}  // max 50 char limit on description field
                          label={description.length > 50 &&
                            <span style={{ color: 'red' }}>
                              Description needs to be less than 50 characters in length
                            </span>
                          }
                        />,
                        // TODO: enable multiple audience/group selection
                        audience: <DynamicPicklist
                          id='audience'
                          query={QUERY_EMPLOYER_BULKMESSAGE_AUDIENCES}
                          params={{ employerId: decodeBase64(this.props.employerId) }}
                          field='name'
                          placeholder='Select existing or add first'
                          value={audience}
                          onChange={audience => this.setState({ audience })}
                          inline={false}
                          useIds={true}
                        />,
                        "compose message": <BulkSMSMessageComposer
                          employerIdentityId={this.props.employerIdentityId}
                          channels={channels}
                          onChannelChange={this.handleChannelChange}
                          onMessageChange={messageContent => this.setState({ messageContent })}
                        />,
                        schedule:
                          <div>
                            {this.state.editScheduledAt
                              ? <div>
                                  <DatePicker
                                    selected={scheduledAt}
                                    onChange={this.updateScheduleAt}
                                    dateFormat='D MMM YYYY'
                                    showTimeSelect={true}
                                    timeFormat='HH:mm'
                                    minDate={moment()}
                                    maxDate={moment().add(60, 'days')}
                                    inline={true}
                                    className='slds-m-top_small'
                                  />
                                  <SvgIcon
                                    title='close'
                                    variant='utility'
                                    symbol='close'
                                    size='x-small'
                                    colour='success'
                                    className='slds-float_right clickable'
                                    onClick={this.toggleEditScheduledAt}
                                  />
                                </div>
                              : <div className='slds-grid' style={{ alignItems: 'center' }}>
                                  {scheduledAt
                                    ? moment(scheduledAt).format('lll')
                                    : 'Will send immediately'
                                  }
                                  <Button className='slds-col_bump-left' onClick={this.toggleEditScheduledAt}>
                                    <SvgIcon
                                      title='edit'
                                      variant='utility'
                                      symbol='edit'
                                      size='x-small'
                                      colour='success'
                                      buttonIcon={true}
                                      className='slds-p-right_small'
                                    />
                                    Edit
                                  </Button>
                                </div>
                            }
                          </div>
                      }}
                    />
                    <div className='slds-m-top_large slds-m-bottom_large'>
                      {/* <Mutation mutation={BULKMESSAGE_MUTATION}>
                         {(bulkmessageMutate, { data }) => (
                           <Button
                            //  disabled={description.length === 0 || message.length === 0}
                             disabled={scheduledAt === null}
                             onClick={() => this.createBulkMessage(bulkmessageMutate)}
                             className='slds-size_1-of-4'>
                             Save
                           </Button>
                         )}
                       </Mutation> */}
                      <Button
                        disabled={!this.requiredFieldsPopulated() || this.state.submitInProgress}
                        onClick={this.togglePreviewModal}
                        className='slds-size_1-of-4'>
                        Preview
                      </Button>
                      <Mutation mutation={BULKMESSAGE_MUTATION}>
                        {(bulkmessageMutate, { data }) => {
                          if (data) {
                            this.props.goToBulkMessageDetailPage(data.bulkmessageMutate.bulkmessage.id)
                          }

                          return (
                            <Button
                              type='brand'
                              disabled={data ||
                                !this.requiredFieldsPopulated() ||
                                this.state.submitInProgress ||
                                this.state.submitComplete}
                              onClick={() => this.createBulkMessage(bulkmessageMutate, refetch)}
                              className='slds-size_1-of-4'>
                              {this.state.submitInProgress
                                ? 'Submitting...'
                                : 'Submit for sending'
                              }
                              {/* consider label as 'save' if no audience member */}
                            </Button>
                          )
                        }}
                      </Mutation>
                    </div>
                  </Fragment>
                )
              }
            }}
          </Query>
          {this.state.showPreviewModal &&
            // cycle through audience identities inside a modal
            <Query query={QUERY_AUDIENCE} variables={{ id: this.state.audience }}>
              {({ loading, error, data}) => {
                if (loading || error) {
                  return null
                }

                // to cycle through identities, break out as component with state to hold identity index
                const identity = data.bulkmessageaudience.identities.edges[0].node

                return (
                  <Modal
                    heading={`Message to ${identity.fullName}`}
                    body={
                      <div
                        className='slds-p-around_small slds-m-top_small'
                        style={{
                          wordWrap: 'break-word',
                          workBreak: 'break-word',
                          whiteSpace: 'pre-line',
                          letterSpacing: '0.75px',
                          fontSize: '1.25em',
                          border: '1px solid grey',
                          borderRadius: '5px',
                          backgroundColor: '#DDECCF'
                        }}>
                        {messageContent
                          .replace("{{firstName}}", identity.firstName)
                          .replace("{{lastName}}", identity.lastName)
                          .replace("{{fullName}}", identity.fullName)
                        }
                      </div>
                    }
                    onCloseClick={this.togglePreviewModal}
                  />
                )
              }}
            </Query>
          }
        </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/messageplus/employer-bulk-sms-message-compose/index.js